/**
 * Study Attachments Dialog — view, download, and upload attachments for a study.
 *
 * Attachments are ObjectItem entries where seruid === "Attachments".
 * They are listed via getObjects (filtering by seruid), downloaded via WADO-URI,
 * uploaded via HTTP POST to /PacsObjectService/{siteId}, and deleted via deleteObjects.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, CircularProgress, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Delete as DeleteIcon,
  Download as DownloadIcon, Upload as UploadIcon,
} from '@mui/icons-material';
import { api } from '../../api/rpc';
import type { Study, DicomObject } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface StudyAttachmentsDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete?: () => void;
}

export const StudyAttachmentsDialog = ({ open, study, onClose, onComplete }: StudyAttachmentsDialogProps) => {
  const [attachments, setAttachments] = useState<DicomObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(async () => {
    if (!study) return;
    setLoading(true);
    try {
      const result = await api.getObjects(study.objKey);
      // Filter to only attachment objects (seruid === "Attachments" or type contains known attachment mime)
      const atts = result.records.filter(
        (obj) => obj.seruid === 'Attachments' || obj.seruid === 'attachments',
      );
      setAttachments(atts);
    } catch (e) {
      pushError('Failed to load attachments: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, [study]);

  useEffect(() => {
    if (open && study) {
      loadAttachments();
      setLabel('');
    }
  }, [open, study, loadAttachments]);

  const handleDownload = (obj: DicomObject) => {
    if (!study) return;
    const session = localStorage.getItem('brit_sid') ?? '';
    const siteId = (study.site ?? study.siteId ?? 'root').replace(/^site:/, '');
    const url = `/PacsObjectService/${encodeURIComponent(siteId)}?requestType=WADO&studyUID=${encodeURIComponent(study.uid)}&objectUID=${encodeURIComponent(obj.id)}&session=${encodeURIComponent(session)}`;
    window.open(url, '_blank');
  };

  const handleDelete = async (obj: DicomObject) => {
    if (!study) return;
    try {
      await api.deleteObjects({ stuKey: study.objKey, objKeys: [obj.objKey] });
      pushSuccess('Attachment deleted');
      await loadAttachments();
      onComplete?.();
    } catch (e) {
      pushError('Failed to delete attachment: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !study) return;

    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('file', files[i]);
      }
      const sid = localStorage.getItem('brit_sid') ?? '';
      const siteId = study.siteId ?? study.site ?? '';
      const url = `/PacsObjectService/${encodeURIComponent(siteId)}?series=Attachments&comment=Uploaded+from+React+UI&label=${encodeURIComponent(label || 'Attachment')}&studyUID=${encodeURIComponent(study.uid)}&sid=${sid}`;
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
      pushSuccess('Attachment uploaded');
      setLabel('');
      await loadAttachments();
      onComplete?.();
    } catch (e) {
      pushError('Failed to upload attachment: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setUploading(false);
      // Clear file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (utc?: number) => {
    if (!utc) return '—';
    return new Date(utc).toLocaleString();
  };

  const getAttachmentLabel = (obj: DicomObject) => {
    // Try details.label (document type) or details.filename, fallback to series description
    const details = obj.details as Record<string, unknown> | undefined;
    return (details?.label as string) ?? (details?.filename as string) ?? obj.details?.sdes ?? obj.type ?? 'Attachment';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Attachments{study ? ` — ${study.name}` : ''}</span>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadAttachments} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : attachments.length === 0 ? (
          <Typography sx={{ py: 2, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
            No attachments for this study
          </Typography>
        ) : (
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Label</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Size</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments.map((obj) => (
                  <TableRow key={obj.objKey} hover>
                    <TableCell sx={{ fontSize: 12 }}>{getAttachmentLabel(obj)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{obj.type ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{formatSize(obj.size)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{formatDate(obj.dateUTC)}</TableCell>
                    <TableCell>
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => handleDownload(obj)}>
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(obj)}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Upload section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <TextField
            label="Label"
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Referral, Clinician Note"
            sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 13 } }}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
          <Button
            size="small"
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
